/**
 * Central registry for the Reports module.
 *
 * Code-facing keys (slug, endpoint, columnas[].key, filtros[].key, permiso)
 * are written in English; user-facing strings (titulo, descripcion,
 * columnas[].label, filtros[].label, categoria) are written in Spanish.
 *
 * Each entry must follow the contract consumed by the generic ReportPage
 * and the reusable ReportTable / ReportFilters / ExportButton components.
 *
 * Filter entries support two shapes:
 *   - string: legacy shorthand (inferred text/date input)
 *   - object: {
 *       key, label,
 *       type: 'text' | 'date' | 'number' | 'select' | 'range',
 *       width?: number,         // pixel width of the control (not the label)
 *       options?, optionsSource?, optionLabel?, optionValue?, placeholder?,
 *       // range-only:
 *       fromKey, toKey, fromPlaceholder?, toPlaceholder?
 *     }
 *
 * Empty filters (including an empty range { from: "", to: "" }) are
 * omitted from the search payload entirely — the backend interprets
 * absence of a filter as "include everything".
 */

const MONTHS_ES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
];

function buildMonthOptions() {
  return MONTHS_ES.map((name, idx) => ({ value: idx + 1, label: name }));
}

function buildFiscalYearOptions(yearsBack = 5) {
  const current = new Date().getFullYear();
  const out = [];
  for (let y = current; y >= current - yearsBack; y--) {
    out.push({ value: y, label: String(y) });
  }
  return out;
}

function buildAccountBalanceColumns(filters) {
  const includeOpening = !!(filters && filters.include_opening_balance);
  const comparative = !!(filters && filters.comparative);

  const cols = [
    {
      key: "accountCode",
      label: "Código cuenta contable",
      columnFilter: {
        param: "levels",
        options: ["Clase", "Grupo", "Cuenta", "Subcuenta", "Auxiliar"]
      }
    },
    { key: "accountName", label: "Nombre cuenta contable", treeColumn: true }
  ];

  if (comparative && includeOpening) {
    cols.push(
      { key: "previousOpeningBalance", label: "Saldo inicial año anterior", format: "decimal", align: "right" },
      { key: "previousYearBalance", label: "Año anterior", format: "decimal", align: "right" },
      { key: "currentOpeningBalance", label: "Saldo inicial año actual", format: "decimal", align: "right" },
      { key: "currentYearBalance", label: "Año actual", format: "decimal", align: "right" }
    );
  } else if (comparative) {
    cols.push(
      { key: "previousYearBalance", label: "Año anterior", format: "decimal", align: "right" },
      { key: "currentYearBalance", label: "Año actual", format: "decimal", align: "right" }
    );
  } else if (includeOpening) {
    cols.push(
      { key: "currentOpeningBalance", label: "Saldo inicial", format: "decimal", align: "right" },
      { key: "currentYearBalance", label: "Año actual", format: "decimal", align: "right" }
    );
  } else {
    cols.push(
      { key: "currentYearBalance", label: "Año actual", format: "decimal", align: "right" }
    );
  }

  return cols;
}

function pad2(n) { return n < 10 ? `0${n}` : String(n); }

// Rango por defecto de TODO reporte con período: el AÑO ACTUAL COMPLETO
// (1-ene a 31-dic). Es función (no valor) para que se reevalúe en cada
// montaje/Limpiar y no se congele si la pestaña queda abierta de un año a otro.
//
// El corte NO es "hasta hoy" a propósito. La base de datos corre en UTC y
// Colombia es UTC-5: pasadas las 7 p.m. local, el servidor ya está en el día
// siguiente y los documentos que se creen quedan fechados "mañana". Con un
// tope en la fecha local, esos movimientos recién registrados caían FUERA del
// filtro y el reporte parecía no actualizarse — sólo reaparecían al ampliar el
// rango a mano. Cerrar en 31-dic cubre el año en curso sin depender de la
// diferencia horaria ni excluir documentos fechados hacia adelante.
function currentYearRange() {
  const y = new Date().getFullYear();
  return { from: `${y}-01-01`, to: `${y}-12-31` };
}

// Shared period filter: a single day/month/year date range emitting
// `start_date` / `end_date` (ISO yyyy-mm-dd). Replaces the older
// fiscal-year + month-range pair on the accounting reports so every
// account/movement report offers full-date precision (like detailed_taxes).
// NOTE: payroll intentionally does NOT use this — its period is bound to
// month + quincena (half-month) semantics that day precision would break.
const periodDateRangeFilter = {
  key: "period_range",
  label: "Período",
  type: "range",
  subtype: "date",
  width: 320,
  fromKey: "start_date",
  toKey: "end_date",
  fromPlaceholder: "Desde",
  toPlaceholder: "Hasta",
  default: currentYearRange
};

const financialReportFilters = [
  periodDateRangeFilter,
  { key: "include_opening_balance", label: "Incluye saldo inicial", type: "boolean", width: 40 },
  { key: "comparative", label: "Comparativo", type: "boolean", width: 40 }
];

const partyInclusionFilters = [
  {
    key: "include_parties",
    label: "Incluir terceros",
    type: "boolean",
    width: 40
  },
  {
    key: "party_identifiers",
    label: "Terceros",
    type: "multiselect",
    width: 320,
    placeholder: "Todos",
    optionsSource: "/paracodes/form_party/",
    optionValue: "codeNumber",
    optionLabel: party => {
      const doc = paracodeDoc(party);
      const id = party.codeNumber || "";
      const name = party.codeTitle || doc.nombre_compuesto || doc.name || "";
      return name ? `${id} - ${name}` : String(id);
    },
    visibleWhen: v => !!(v && v.include_parties === true)
  }
];

const trialBalanceFilters = [
  {
    key: "account_range",
    label: "Cuenta contable",
    type: "range",
    width: 540,
    fromKey: "account_from",
    toKey: "account_to",
    fromPlaceholder: "Inicial",
    toPlaceholder: "Final",
    optionsSource: "/paracodes/form_account/",
    optionLabel: account => `${account.code} - ${account.name}`,
    optionValue: "code"
  },
  periodDateRangeFilter,
  ...partyInclusionFilters
];

function paracodeDoc(record) {
  if (!record) return {};
  let doc = record.doc != null ? record.doc : record;
  if (typeof doc === "string") {
    try { doc = JSON.parse(doc); } catch (e) { doc = record; }
  }
  return doc || {};
}

function distinctTaxOptions(values) {
  const seen = new Set();
  const out = [];
  (values || []).forEach(v => {
    if (v == null || v === "" || seen.has(v)) return;
    seen.add(v);
    out.push({ value: v, label: String(v) });
  });
  out.sort((a, b) => String(a.label).localeCompare(String(b.label), "es", { numeric: true }));
  return out;
}

const detailedTaxesFilters = [
  {
    key: "tax_types",
    label: "Tipo de impuesto",
    type: "multiselect",
    width: 240,
    placeholder: "Todos",
    optionsSource: "/paracodes/form_tax/",
    optionsBuilder: raw => {
      const byValue = new Map();
      (raw || []).forEach(r => {
        const doc = paracodeDoc(r);
        const v = doc.tax_type;
        if (v == null || v === "") return;
        if (!byValue.has(v)) {
          const label = doc["Tipo de impuesto"] || String(v).toUpperCase();
          byValue.set(v, label);
        }
      });
      return Array.from(byValue.entries())
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => String(a.label).localeCompare(String(b.label), "es", { numeric: true }));
    }
  },
  {
    key: "tax_names",
    label: "Porcentaje impuesto",
    type: "multiselect",
    width: 240,
    placeholder: "Todos",
    optionsSource: "/paracodes/form_tax/",
    dependsOn: ["tax_types"],
    optionsBuilder: (raw, values) => {
      const selectedTypes = Array.isArray(values && values.tax_types) ? values.tax_types : [];
      const records = (raw || []).filter(r => {
        if (!selectedTypes.length) return true;
        return selectedTypes.includes(paracodeDoc(r).tax_type);
      });
      return distinctTaxOptions(records.map(r => paracodeDoc(r).name));
    }
  },
  periodDateRangeFilter,
  {
    key: "party_identifier",
    label: "Tercero",
    type: "select",
    width: 320,
    placeholder: "Todos",
    optionsSource: "/paracodes/form_party/",
    optionValue: "codeNumber",
    optionLabel: party => {
      const doc = paracodeDoc(party);
      const id = party.codeNumber || "";
      const name = party.codeTitle || doc.nombre_compuesto || doc.name || "";
      return name ? `${id} - ${name}` : String(id);
    }
  }
];

const detailedTaxesColumns = [
  { key: "taxType", label: "Tipo de impuesto", hideInTable: true },
  { key: "taxName", label: "Porcentaje impuesto", hideInTable: true },
  { key: "transactionNumber", label: "Comprobante" },
  { key: "partyIdentifier", label: "Identificación" },
  { key: "partyName", label: "Nombre tercero" },
  { key: "movementDate", label: "Fecha elaboración", format: "date" },
  { key: "baseVentas", label: "Base ventas", format: "decimal", align: "right" },
  { key: "valorImpuestoVentas", label: "Valor impuesto ventas", format: "decimal", align: "right" },
  { key: "baseCompras", label: "Base compras", format: "decimal", align: "right" },
  { key: "valorImpuestoCompras", label: "Valor impuesto compras", format: "decimal", align: "right" },
  { key: "baseDevolucionVentas", label: "Base devolución ventas", format: "decimal", align: "right" },
  { key: "valorImpuestoDevolucionVentas", label: "Valor impuesto devolución ventas", format: "decimal", align: "right" },
  { key: "baseDevolucionCompras", label: "Base devolución compras", format: "decimal", align: "right" },
  { key: "valorImpuestoDevolucionCompras", label: "Valor impuesto devolución compras", format: "decimal", align: "right" }
];

// ---------- Comprobantes ----------
// Filtros compartidos por los tres reportes de comprobantes: tipo de
// transacción (catálogo form_transactiontype) + rango de fechas.
function buildVoucherFilters(withCurrentYearDefault) {
  const period = {
    key: "period_range",
    label: "Período",
    type: "range",
    subtype: "date",
    width: 320,
    fromKey: "start_date",
    toKey: "end_date",
    fromPlaceholder: "Desde",
    toPlaceholder: "Hasta"
  };
  if (withCurrentYearDefault) period.default = currentYearRange;
  return [
    {
      key: "transaction_types",
      label: "Tipo de transacción",
      type: "multiselect",
      width: 320,
      placeholder: "Todos",
      optionsSource: "/paracodes/form_transactiontype/",
      optionValue: "codeNumber",
      optionLabel: tt => {
        const doc = paracodeDoc(tt);
        const code = tt.codeNumber || "";
        const name = tt.codeTitle || doc.name || "";
        return name ? `${code} - ${name}` : String(code);
      }
    },
    period
  ];
}

// ---------- Cartera: cuentas por pagar y por cobrar ----------
// Ambas familias comparten filtros y columnas: sólo cambian las etiquetas
// (proveedor/cliente) y el alcance de cuentas, que se resuelve en el backend.
// Los buckets deben coincidir EXACTAMENTE con AGING_BUCKETS del validador
// accountsAging.validator.js y con el CASE de las plantillas SQL.
const agingBucketOptions = [
  { value: "por_vencer", label: "Saldo por vencer" },
  { value: "1_30", label: "Vencido 1 a 30" },
  { value: "31_60", label: "Vencido 31 a 60" },
  { value: "61_90", label: "Vencido 61 a 90" },
  { value: "mas_91", label: "Vencido más de 91" },
  { value: "a_favor", label: "Saldo a favor" }
];

// Filtros de cartera. `partyLabel` cambia sólo la etiqueta (Proveedor/Cliente);
// el backend ya sabe si el reporte es de pagar o de cobrar.
function buildAgingFilters(partyLabel) {
  return [
    {
      key: "party_identifiers",
      label: partyLabel,
      type: "multiselect",
      width: 320,
      placeholder: "Todos",
      optionsSource: "/paracodes/form_party/",
      optionValue: "codeNumber",
      optionLabel: party => {
        const doc = paracodeDoc(party);
        const id = party.codeNumber || "";
        const name = party.codeTitle || doc.nombre_compuesto || doc.name || "";
        return name ? `${id} - ${name}` : String(id);
      }
    },
    {
      // "Plazo" = rango de vencimiento; los mismos buckets que las columnas.
      key: "aging_buckets",
      label: "Plazo",
      type: "multiselect",
      width: 260,
      placeholder: "Todos",
      options: agingBucketOptions
    }
  ];
}

// Columnas de dinero compartidas por los cuatro reportes de cartera con edades.
// El saldo a favor viene NEGATIVO a propósito, para que las columnas sumen
// exactamente el total del tercero y la fila cuadre.
function buildAgingAmountColumns(totalLabel) {
  return [
    { key: "overdue1To30", label: "Vencido 1 a 30", format: "decimal", align: "right", sortable: true },
    { key: "overdue31To60", label: "Vencido 31 a 60", format: "decimal", align: "right", sortable: true },
    { key: "overdue61To90", label: "Vencido 61 a 90", format: "decimal", align: "right", sortable: true },
    { key: "overdue91Plus", label: "Vencido más de 91", format: "decimal", align: "right", sortable: true },
    { key: "notDueBalance", label: "Saldo por vencer", format: "decimal", align: "right", sortable: true },
    { key: "creditBalance", label: "Saldo a favor", format: "decimal", align: "right", sortable: true },
    { key: "partyTotal", label: totalLabel, format: "decimal", align: "right", sortable: true }
  ];
}

// Consolidado por tercero: el nombre lleva drill-down al detalle por documento.
function buildAgingByPartyColumns({ partyLabel, totalLabel, documentReport }) {
  return [
    { key: "partyId", label: `NIT ${partyLabel.toLowerCase()}`, hideInTable: true },
    {
      key: "party",
      label: partyLabel,
      sortable: true,
      sticky: true,
      width: 280,
      link: row => (row && row.partyId
        ? { report: documentReport, filters: { party_identifiers: [row.partyId] } }
        : null)
    },
    ...buildAgingAmountColumns(totalLabel)
  ];
}

// Detalle por documento: el número de documento abre el comprobante original
// (/cases/<docId>) en una pestaña nueva.
function buildAgingByDocumentColumns({ partyLabel, totalLabel }) {
  return [
    { key: "partyId", label: `NIT ${partyLabel.toLowerCase()}`, hideInTable: true },
    { key: "party", label: partyLabel, sortable: true, sticky: true, width: 280 },
    { key: "docId", label: "docId", hideInTable: true },
    {
      key: "document",
      label: "Documento",
      sortable: true,
      href: row => (row && row.docId ? `/cases/${row.docId}` : null),
      hrefTitle: "Abrir el documento en una pestaña nueva"
    },
    { key: "dueDate", label: "Fecha de vencimiento", format: "date", sortable: true },
    // Disponibles en el Excel, fuera de la tabla para no recargarla.
    { key: "documentDate", label: "Fecha documento", format: "date", hideInTable: true },
    { key: "daysOverdue", label: "Días vencido", format: "number", align: "right", hideInTable: true },
    ...buildAgingAmountColumns(totalLabel)
  ];
}

// ---------- Compras ----------
// Columnas de valores compartidas por los tres reportes de compras.
const purchaseAmountColumns = [
  { key: "grossValue", label: "Valor bruto", format: "decimal", align: "right", sortable: true },
  { key: "chargeTax", label: "Impuesto cargo", format: "decimal", align: "right", sortable: true },
  { key: "withholdingTax", label: "Impuesto retención", format: "decimal", align: "right", sortable: true },
  { key: "total", label: "Total", format: "decimal", align: "right", sortable: true }
];

const purchaseSupplierFilter = {
  key: "party_identifiers",
  label: "Proveedor",
  type: "multiselect",
  width: 320,
  placeholder: "Todos",
  optionsSource: "/paracodes/form_party/",
  optionValue: "codeNumber",
  optionLabel: party => {
    const doc = paracodeDoc(party);
    const id = party.codeNumber || "";
    const name = party.codeTitle || doc.nombre_compuesto || doc.name || "";
    return name ? `${id} - ${name}` : String(id);
  }
};

// Drill-down al subreporte: se lleva el proveedor y el rango de fechas vigente
// (el rango viaja solo, porque también es filtro declarado del destino).
const purchaseDrillToDocuments = row => (row && row.supplierId
  ? { report: "purchases_by_supplier_documents", filters: { party_identifiers: [row.supplierId] } }
  : null);

function currentYearNow() { return new Date().getFullYear(); }
function currentMonthNow() { return new Date().getMonth() + 1; }
function currentQuincenaNow() { return new Date().getDate() <= 15 ? "primera_quincena" : "segunda_quincena"; }

function payrollTypeBodyClass(type) {
  if (type === "deduccion") return "is-deduction";
  return null;
}
function payrollTypeHeaderClass(type) {
  if (type === "deduccion") return "h-is-deduction";
  if (type === "devengado") return "h-is-earning";
  if (type === "prestacion") return "h-is-benefit";
  if (type === "causacion") return "h-is-accrual";
  return null;
}

function buildPayrollAccumulatedColumns(filters, meta) {
  const fixed = [
    { key: "contractNumber", label: "Número de contrato", sticky: true, width: 150 },
    { key: "employeeFullName", label: "Empleado", sticky: true, width: 220 },
    { key: "salary", label: "Sueldo", format: "decimal", align: "right", sticky: true, width: 140 }
  ];
  const dyn = (meta && Array.isArray(meta.dynamicColumns)) ? meta.dynamicColumns : [];
  const dynamic = dyn.map(c => ({
    key: c.key,
    label: c.label || c.key,
    format: "decimal",
    align: "right",
    headerClass: payrollTypeHeaderClass(c.type),
    cellClass: payrollTypeBodyClass(c.type),

    accessor: row => {
      const m = row && row.dynamicConcepts;
      if (m && Object.prototype.hasOwnProperty.call(m, c.key)) return m[c.key];
      return row ? row[c.key] : null;
    }
  }));
  return fixed.concat(dynamic);
}

// Período (año/mes inicial y final) y selector de empleado: compartidos por
// los reportes de nómina para evitar duplicación.
const payrollPeriodGroupFilters = [
  {
    key: "start_year",
    label: "Año",
    type: "select",
    group: "start_period",
    groupLabel: "Período inicial",
    width: 100,
    options: buildFiscalYearOptions(5),
    default: currentYearNow
  },
  {
    // Enero, para que el período por defecto sea "del año actual hasta la
    // fecha" (enero → mes en curso), igual que el rango de los contables.
    key: "start_month",
    label: "Mes",
    type: "select",
    group: "start_period",
    width: 140,
    options: buildMonthOptions(),
    default: 1
  },
  {
    key: "end_year",
    label: "Año",
    type: "select",
    group: "end_period",
    groupLabel: "Período final",
    width: 100,
    options: buildFiscalYearOptions(5),
    default: currentYearNow
  },
  {
    key: "end_month",
    label: "Mes",
    type: "select",
    group: "end_period",
    width: 140,
    options: buildMonthOptions(),
    default: currentMonthNow
  }
];

const payrollEmployeeFilter = {
  key: "employee_id",
  label: "Empleado",
  type: "select",
  width: 320,
  placeholder: "Todos",
  optionsSource: "/paracodes/form_employee/?codesysstate=NORMAL",
  optionValue: "codeNumber",
  optionLabel: emp => {
    const doc = paracodeDoc(emp);
    const composed = doc.nombre_compuesto;
    if (composed) return String(composed);
    const id = emp.codeNumber || "";
    const name = emp.codeTitle || doc.name || doc.full_name || "";
    return name ? `${id} - ${name}` : String(id);
  }
};

const payrollAccumulatedFilters = [
  ...payrollPeriodGroupFilters,
  {
    key: "payroll_period",
    label: "Quincena",
    type: "select",
    width: 200,
    placeholder: "Ambas",
    options: [
      { value: "primera_quincena", label: "Primera quincena" },
      { value: "segunda_quincena", label: "Segunda quincena" }
    ],

    visibleWhen: v => {
      if (!v) return false;
      const sy = Number(v.start_year), ey = Number(v.end_year);
      const sm = Number(v.start_month), em = Number(v.end_month);
      return Number.isFinite(sy) && Number.isFinite(ey)
        && Number.isFinite(sm) && Number.isFinite(em)
        && sy === ey && sm === em;
    }
  },
  payrollEmployeeFilter
];

const payrollSocialSecurityFilters = [
  ...payrollPeriodGroupFilters,
  payrollEmployeeFilter
];

// Una fila por empleado. Money en decimal; % ARL en percentage; banderas de
// exoneración en boolean. Las dos primeras columnas (código y nombre) quedan
// fijas (sticky) para navegar el ancho del reporte.
const payrollSocialSecurityColumns = [
  { key: "employeeName", label: "Empleado", sortable: true, sticky: true, width: 220 },
  { key: "contractNumber", label: "Número de contrato", sortable: true },
  { key: "payrollNumber", label: "Número de nómina", sortable: true },

  // Salud
  { key: "healthFund", label: "Fondo de salud", sortable: true },
  { key: "totalEarningsSocialSecurity", label: "Total devengado seguridad social", format: "decimal", align: "right", sortable: true },
  { key: "ibcHealthEmployee", label: "IBC salud empleado", format: "decimal", align: "right" },
  { key: "ibcHealthEmployer", label: "IBC salud empleador", format: "decimal", align: "right" },
  { key: "employeeHealthContribution", label: "Aporte salud empleado", format: "decimal", align: "right", sortable: true },
  { key: "employerHealthContribution", label: "Aporte salud empleador", format: "decimal", align: "right", sortable: true },
  { key: "exonerated", label: "Exonerado", format: "boolean" },

  // Pensión
  { key: "pensionFund", label: "Fondo de pensión", sortable: true },
  { key: "ibcPensionEmployee", label: "IBC pensión empleado", format: "decimal", align: "right" },
  { key: "ibcPensionEmployer", label: "IBC pensión empleador", format: "decimal", align: "right" },
  { key: "employeePensionContribution", label: "Aporte pensión empleado", format: "decimal", align: "right", sortable: true },
  { key: "employerPensionContribution", label: "Aporte pensión empleador", format: "decimal", align: "right", sortable: true },
  { key: "publicPensionContribution", label: "Aporte pensión pública", format: "decimal", align: "right", sortable: true },
  { key: "accaiPensionContribution", label: "Aporte pensión ACCAI", format: "decimal", align: "right", sortable: true },
  { key: "solidarityPensionFund", label: "Fondo de solidaridad pensional (FSP)", format: "decimal", align: "right", sortable: true },

  // Riesgos laborales (ARL)
  { key: "arlAdministrator", label: "ARL", sortable: true },
  { key: "ibcArl", label: "IBC ARL", format: "decimal", align: "right" },
  { key: "arlPercentage", label: "% ARL", format: "percentage", align: "right" },
  { key: "employerArlContribution", label: "Aporte ARL empleador", format: "decimal", align: "right", sortable: true },

  // Parafiscales
  { key: "ibcParafiscales", label: "IBC parafiscales", format: "decimal", align: "right" },
  { key: "exoneratedParafiscales", label: "Exonerado parafiscales", format: "boolean" },
  { key: "compensationFund", label: "Caja de compensación", sortable: true },
  { key: "compensationFundContribution", label: "Aporte caja de compensación", format: "decimal", align: "right", sortable: true },
  { key: "icbfContribution", label: "ICBF", format: "decimal", align: "right", sortable: true },
  { key: "senaContribution", label: "SENA", format: "decimal", align: "right", sortable: true }
];

// Archivo de pago electrónico de nómina: un banco + un período. `bank_code` es
// requerido (ReportFilters bloquea la búsqueda). El período es mes + quincena.
const payrollBankPaymentFilters = [
  {
    key: "bank_code",
    label: "Banco",
    type: "select",
    required: true,
    width: 260,
    placeholder: "Seleccione un banco",
    optionsSource: "/paracodes/form_bank/",
    optionValue: "codeNumber",
    optionLabel: bank => {
      const doc = paracodeDoc(bank);
      const code = bank.codeNumber || "";
      const name = bank.codeTitle || doc.name || doc.nombre_compuesto || "";
      return name ? `${code} - ${name}` : String(code);
    }
  },
  { key: "fiscal_year", label: "Año", type: "select", width: 110, options: buildFiscalYearOptions(5), default: currentYearNow },
  { key: "month", label: "Mes", type: "select", width: 150, options: buildMonthOptions(), default: currentMonthNow },
  {
    key: "payroll_period",
    label: "Quincena",
    type: "select",
    width: 200,
    default: currentQuincenaNow,
    options: [
      { value: "primera_quincena", label: "Primera quincena" },
      { value: "segunda_quincena", label: "Segunda quincena" }
    ]
  },
  {
    key: "employee_ids",
    label: "Empleados",
    type: "multiselect",
    width: 320,
    placeholder: "Todos",
    optionsSource: "/paracodes/form_employee/?codesysstate=NORMAL",
    optionValue: "codeNumber",
    optionLabel: emp => {
      const doc = paracodeDoc(emp);
      const composed = doc.nombre_compuesto;
      if (composed) return String(composed);
      const id = emp.codeNumber || "";
      const name = emp.codeTitle || doc.name || doc.full_name || "";
      return name ? `${id} - ${name}` : String(id);
    }
  }
];

const payrollBankPaymentColumns = [
  { key: "identificationType", label: "Tipo identificación", sortable: true },
  { key: "identificationNumber", label: "Número identificación", sortable: true },
  { key: "firstName", label: "Nombre", sortable: true },
  { key: "lastName", label: "Apellido", sortable: true },
  { key: "bankCode", label: "Código banco", sortable: true },
  { key: "accountType", label: "Tipo cuenta", sortable: true },
  { key: "accountNumber", label: "Número cuenta", sortable: true },
  { key: "paymentAmount", label: "Valor pago", format: "decimal", align: "right", sortable: true },
  { key: "reference", label: "Referencia", sortable: true },
  { key: "email", label: "Correo electrónico" },
  { key: "status", label: "Estado", sortable: true },
  { key: "observations", label: "Observaciones" }
];

function buildTrialBalanceColumns(filters) {
  const includeParties = !!(filters && filters.include_parties);
  const cols = [
    { key: "level", label: "Nivel", sortable: true, columnFilter: { param: "levels", options: ["Clase", "Grupo", "Cuenta", "Subcuenta", "Auxiliar"] } },
    { key: "isTransactional", label: "Transaccional", sortable: true, format: "boolean" },
    { key: "accountCode", label: "Código cuenta contable", sortable: true },
    { key: "accountName", label: "Nombre cuenta contable", sortable: true }
  ];
  if (includeParties) {
    cols.push(
      { key: "partyIdentifier", label: "NIT", sortable: true },
      { key: "partyName", label: "Tercero", sortable: true }
    );
  }
  cols.push(
    { key: "openingBalance", label: "Saldo inicial", sortable: true, format: "decimal" },
    { key: "debitMovement", label: "Movimiento débito", sortable: true, format: "decimal" },
    { key: "creditMovement", label: "Movimiento crédito", sortable: true, format: "decimal" },
    {
      key: "closingBalance",
      label: "Saldo final",
      sortable: true,
      format: "decimal",
      link: row => {
        if (!row || row.level !== "Auxiliar" || !row.accountCode) return null;
        const drill = { account_code: row.accountCode };
        if (includeParties && row.partyIdentifier) {
          drill.include_parties = true;
          drill.party_identifiers = [row.partyIdentifier];
        }
        return { report: "movimiento_auxiliar", filters: drill };
      }
    }
  );
  return cols;
}

function buildAuxiliaryLedgerColumns(filters) {
  const includeParties = !!(filters && filters.include_parties);
  const idCol = includeParties
    ? { key: "partyIdentifier", label: "NIT", sortable: true }
    : { key: "identification", label: "Identificación", sortable: true };
  const nameCol = includeParties
    ? { key: "partyName", label: "Tercero", sortable: true }
    : { key: "thirdPartyName", label: "Nombre del tercero", sortable: true };
  return [
    { key: "accountCode", label: "Código cuenta contable", sortable: true },
    { key: "accountName", label: "Nombre cuenta contable", sortable: true },
    { key: "voucher", label: "Comprobante", sortable: true },
    { key: "sequence", label: "Secuencia", sortable: true, format: "number" },
    { key: "elaborationDate", label: "Fecha elaboración", sortable: true, format: "date" },
    idCol,
    { key: "branch", label: "Sucursal", sortable: true },
    nameCol,
    { key: "description", label: "Descripción" },
    { key: "detail", label: "Detalle" },
    { key: "costCenter", label: "Centro de costo", sortable: true },
    { key: "openingBalance", label: "Saldo inicial", sortable: true, format: "decimal" },
    { key: "debit", label: "Débito", sortable: true, format: "decimal" },
    { key: "credit", label: "Crédito", sortable: true, format: "decimal" },
    { key: "movementBalance", label: "Saldo movimiento", sortable: true, format: "decimal" },
    { key: "accountTotalBalance", label: "Saldo total cuenta", sortable: true, format: "decimal" }
  ];
}

// ── Activos fijos ────────────────────────────────────────────────────────────
// El backend de depreciación habla en periodos `YYYYMM`, no en fechas. Se
// ofrecen como lista desplegable en vez de un campo libre: el usuario elige
// «Agosto 2026» y viaja "202608", sin margen para teclear un periodo inválido.
function buildPeriodOptions({ back = 36, forward = 12 } = {}) {
  const now = new Date();
  const options = [];
  for (let offset = forward; offset >= -back; offset--) {
    const d = new Date(now.getFullYear(), now.getMonth() - offset, 1);
    const value = `${d.getFullYear()}${pad2(d.getMonth() + 1)}`;
    options.push({ value, label: `${MONTHS_ES[d.getMonth()]} ${d.getFullYear()}` });
  }
  return options;
}

function currentPeriodCode() {
  const now = new Date();
  return `${now.getFullYear()}${pad2(now.getMonth() + 1)}`;
}

const bookFilter = {
  key: "book",
  label: "Libro",
  type: "select",
  width: 200,
  options: [
    { value: "accounting", label: "Contable (NIIF)" },
    { value: "tax", label: "Fiscal (art. 137 E.T.)" }
  ],
  default: "accounting"
};

const fixedAssetScopeFilters = [
  {
    key: "group_codes",
    label: "Grupos",
    type: "multiselect",
    width: 280,
    placeholder: "Todos",
    optionsSource: "/paracodes/form_fixedassetgroup/",
    optionValue: "codeNumber",
    optionLabel: group => {
      const doc = paracodeDoc(group);
      const code = group.codeNumber || "";
      const name = group.codeTitle || doc.name || "";
      return name ? `${code} - ${name}` : String(code);
    }
  },
  {
    key: "asset_codes",
    label: "Activos",
    type: "multiselect",
    width: 280,
    placeholder: "Todos",
    optionsSource: "/paracodes/form_fixedasset/",
    optionValue: "codeNumber",
    optionLabel: asset => {
      const doc = paracodeDoc(asset);
      const code = asset.codeNumber || "";
      const name = asset.codeTitle || doc.name || "";
      return name ? `${code} - ${name}` : String(code);
    }
  }
];

const reports = [
  {
    slug: "general_trial_balance",
    titulo: "Balance de prueba general",
    descripcion: "Saldos iniciales, movimientos y saldos finales por cuenta contable.",
    endpoint: "reports/general-trial-balance",
    categoria: "Contabilidad",
    columnas: buildTrialBalanceColumns,
    filtros: trialBalanceFilters,
    exportable: true,
    paginated: true,
    defaultPageSize: 50,
    summaryColumnMap: { debit: "debitMovement", credit: "creditMovement" },
    permiso: "reports.general_trial_balance.view",
  },
  {
    slug: "movimiento_auxiliar",
    titulo: "Movimiento auxiliar por cuenta contable",
    descripcion: "Detalle de movimientos contables por cuenta auxiliar.",
    endpoint: "reports/movimiento-auxiliar",
    categoria: "Contabilidad",
    columnas: buildAuxiliaryLedgerColumns,
    filtros: trialBalanceFilters,
    exportable: true,
    paginated: true,
    defaultPageSize: 50,
    permiso: "reports.movimiento_auxiliar.view"
  },
  {
    slug: "financial_position",
    titulo: "Estado de situación financiera",
    descripcion: "Activos, pasivos y patrimonio con comparativos opcionales por año.",
    endpoint: "reports/financial-position",
    categoria: "Contabilidad",
    columnas: buildAccountBalanceColumns,
    filtros: financialReportFilters,
    exportable: true,
    paginated: true,
    defaultPageSize: 200,
    permiso: "reports.financial_position.view"
  },
  {
    slug: "income_statement",
    titulo: "Estado de resultado integral",
    descripcion: "Ingresos, gastos y costos con comparativos opcionales por año.",
    endpoint: "reports/income-statement",
    categoria: "Contabilidad",
    columnas: buildAccountBalanceColumns,
    filtros: financialReportFilters,
    exportable: true,
    paginated: true,
    defaultPageSize: 200,
    permiso: "reports.income_statement.view"
  },
  {
    slug: "detailed_taxes",
    titulo: "Impuestos detallados",
    descripcion: "Movimientos de impuestos agrupados por tipo y porcentaje de impuesto.",
    endpoint: "reports/detailed-taxes",
    categoria: "Contabilidad",
    columnas: detailedTaxesColumns,
    filtros: detailedTaxesFilters,
    grouping: {
      levels: [
        { key: "taxType", className: "is-group-level-0" },
        { key: "taxName", className: "is-group-level-1" }
      ]
    },
    exportable: true,
    paginated: true,
    defaultPageSize: 200,
    autoLoad: true,
    permiso: "reports.detailed_taxes.view"
  },
  {
    slug: "payroll_general_accumulated",
    titulo: "Acumulados generales de nómina",
    descripcion: "Acumulado de conceptos de nómina por empleado en el período seleccionado.",
    endpoint: "reports/payroll-general-accumulated",
    categoria: "Nómina",
    columnas: buildPayrollAccumulatedColumns,
    filtros: payrollAccumulatedFilters,
    exportable: true,
    paginated: true,
    defaultPageSize: 200,
    stickyHeader: true,
    permiso: "reports.payroll_general_accumulated.view"
  },
  {
    slug: "payroll_social_security",
    titulo: "Seguridad Social y Parafiscales",
    descripcion: "Aportes de seguridad social (salud, pensión, ARL) y parafiscales consolidados por empleado en el período.",
    endpoint: "reports/payroll-social-security",
    categoria: "Nómina",
    columnas: payrollSocialSecurityColumns,
    filtros: payrollSocialSecurityFilters,
    exportable: true,
    paginated: true,
    defaultPageSize: 200,
    stickyHeader: true,
    permiso: "reports.payroll_social_security.view"
  },
  {
    slug: "payroll_bank_payment_file",
    titulo: "Archivo de pago electrónico de nómina",
    descripcion: "Dataset de pago por banco y período — base reutilizable para exportar archivos bancarios (TXT, ACH, Excel).",
    endpoint: "reports/payroll-bank-payment-file",
    categoria: "Nómina",
    columnas: payrollBankPaymentColumns,
    filtros: payrollBankPaymentFilters,
    exportable: true,
    paginated: true,
    defaultPageSize: 200,
    stickyHeader: true,
    permiso: "reports.payroll_bank_payment_file.view"
  },
  {
    slug: "accounts_payable_by_supplier",
    titulo: "Cuentas por pagar general por proveedor",
    descripcion: "Cartera de proveedores por edades de vencimiento, consolidada por proveedor.",
    endpoint: "reports/accounts-payable-by-supplier",
    categoria: "Cuentas por pagar",
    columnas: buildAgingByPartyColumns({
      partyLabel: "Proveedor",
      totalLabel: "Total proveedor",
      documentReport: "accounts_payable_by_document"
    }),
    filtros: buildAgingFilters("Proveedor"),
    exportable: true,
    paginated: true,
    defaultPageSize: 200,
    stickyHeader: true,
    // Cartera abierta a hoy: sin filtros se muestra toda.
    autoLoad: true,
    permiso: "reports.accounts_payable_by_supplier.view"
  },
  {
    slug: "accounts_payable_by_document",
    titulo: "Cuentas por pagar detallada por documento",
    descripcion: "Cartera de proveedores por edades de vencimiento, con una fila por documento.",
    endpoint: "reports/accounts-payable-by-document",
    categoria: "Cuentas por pagar",
    columnas: buildAgingByDocumentColumns({
      partyLabel: "Proveedor",
      totalLabel: "Total proveedor"
    }),
    filtros: buildAgingFilters("Proveedor"),
    exportable: true,
    paginated: true,
    defaultPageSize: 200,
    stickyHeader: true,
    autoLoad: true,
    permiso: "reports.accounts_payable_by_document.view"
  },
  {
    slug: "accounts_receivable_by_client",
    titulo: "Cuentas por cobrar general por cliente",
    descripcion: "Cartera de clientes por edades de vencimiento, consolidada por cliente.",
    endpoint: "reports/accounts-receivable-by-client",
    categoria: "Cuentas por cobrar",
    columnas: buildAgingByPartyColumns({
      partyLabel: "Cliente",
      totalLabel: "Total cliente",
      documentReport: "accounts_receivable_by_document"
    }),
    filtros: buildAgingFilters("Cliente"),
    exportable: true,
    paginated: true,
    defaultPageSize: 200,
    stickyHeader: true,
    autoLoad: true,
    permiso: "reports.accounts_receivable_by_client.view"
  },
  {
    slug: "accounts_receivable_by_document",
    titulo: "Cuentas por cobrar detallada por documento",
    descripcion: "Cartera de clientes por edades de vencimiento, con una fila por documento.",
    endpoint: "reports/accounts-receivable-by-document",
    categoria: "Cuentas por cobrar",
    columnas: buildAgingByDocumentColumns({
      partyLabel: "Cliente",
      totalLabel: "Total cliente"
    }),
    filtros: buildAgingFilters("Cliente"),
    exportable: true,
    paginated: true,
    defaultPageSize: 200,
    stickyHeader: true,
    autoLoad: true,
    permiso: "reports.accounts_receivable_by_document.view"
  },
  {
    slug: "accounts_receivable_as_of_date",
    titulo: "Cuentas por cobrar con corte a una fecha",
    descripcion: "Saldo de cartera de clientes tal como estaba en la fecha de corte.",
    endpoint: "reports/accounts-receivable-as-of-date",
    categoria: "Cuentas por cobrar",
    columnas: [
      { key: "partyId", label: "NIT cliente", hideInTable: true },
      { key: "party", label: "Cliente", sortable: true, sticky: true, width: 280 },
      { key: "receivableDue", label: "Deuda por cobrar a la fecha", format: "decimal", align: "right", sortable: true },
      { key: "creditBalance", label: "Saldo a favor", format: "decimal", align: "right", sortable: true },
      { key: "portfolioBalance", label: "Saldo cartera", format: "decimal", align: "right", sortable: true }
    ],
    filtros: [
      {
        key: "party_identifiers",
        label: "Cliente",
        type: "multiselect",
        width: 320,
        placeholder: "Todos",
        optionsSource: "/paracodes/form_party/",
        optionValue: "codeNumber",
        optionLabel: party => {
          const doc = paracodeDoc(party);
          const id = party.codeNumber || "";
          const name = party.codeTitle || doc.nombre_compuesto || doc.name || "";
          return name ? `${id} - ${name}` : String(id);
        }
      },
      {
        key: "cutoff_date",
        label: "Fecha de corte",
        type: "date",
        width: 190,
        // Por defecto hoy: el saldo "a la fecha".
        default: () => {
          const n = new Date();
          return `${n.getFullYear()}-${pad2(n.getMonth() + 1)}-${pad2(n.getDate())}`;
        }
      }
    ],
    exportable: true,
    paginated: true,
    defaultPageSize: 200,
    stickyHeader: true,
    autoLoad: true,
    permiso: "reports.accounts_receivable_as_of_date.view"
  },
  {
    slug: "purchases_by_supplier",
    titulo: "Compras por proveedor",
    descripcion: "Compras consolidadas por proveedor en el rango de fechas.",
    endpoint: "reports/purchases-by-supplier",
    categoria: "Compras",
    columnas: [
      { key: "supplierId", label: "NIT proveedor", hideInTable: true },
      {
        key: "supplier",
        label: "ID proveedor",
        sortable: true,
        sticky: true,
        width: 300,
        link: purchaseDrillToDocuments
      },
      {
        key: "voucherCount",
        label: "# Comprobantes",
        format: "number",
        align: "right",
        sortable: true,
        link: purchaseDrillToDocuments
      },
      ...purchaseAmountColumns
    ],
    filtros: [purchaseSupplierFilter, periodDateRangeFilter],
    exportable: true,
    paginated: true,
    defaultPageSize: 200,
    stickyHeader: true,
    autoLoad: true,
    permiso: "reports.purchases_by_supplier.view"
  },
  {
    slug: "purchases_by_supplier_documents",
    titulo: "Compras por proveedor - Detalle de comprobantes",
    descripcion: "Comprobantes de compra que componen el consolidado por proveedor.",
    endpoint: "reports/purchases-by-supplier-documents",
    categoria: "Compras",
    columnas: [
      { key: "supplierId", label: "ID proveedor", sortable: true },
      { key: "supplier", label: "Proveedor", sortable: true, sticky: true, width: 300 },
      { key: "voucher", label: "# Comprobante", sortable: true },
      { key: "docId", label: "docId", hideInTable: true },
      {
        // Abre la factura del proveedor en una pestaña nueva.
        key: "supplierInvoice",
        label: "Factura proveedor",
        sortable: true,
        href: row => (row && row.docId ? `/cases/${row.docId}` : null),
        hrefTitle: "Abrir el documento en una pestaña nueva"
      },
      { key: "invoiceDate", label: "Fecha factura", format: "date", sortable: true },
      ...purchaseAmountColumns
    ],
    filtros: [purchaseSupplierFilter, periodDateRangeFilter],
    exportable: true,
    paginated: true,
    defaultPageSize: 200,
    stickyHeader: true,
    autoLoad: true,
    permiso: "reports.purchases_by_supplier.view"
  },
  {
    slug: "purchases_by_product",
    titulo: "Compras por producto",
    descripcion: "Compras consolidadas por producto o concepto en el rango de fechas.",
    endpoint: "reports/purchases-by-product",
    categoria: "Compras",
    columnas: [
      { key: "productId", label: "Código producto", hideInTable: true },
      { key: "product", label: "Producto", sortable: true, sticky: true, width: 320 },
      { key: "inventoryGroup", label: "Grupo de inventario", sortable: true },
      { key: "voucherCount", label: "# Comprobantes", format: "number", align: "right", sortable: true },
      ...purchaseAmountColumns
    ],
    filtros: [
      {
        // Los conceptos comprados son modelos contables (form_accountingmodel):
        // el mismo catálogo que referencian las líneas de la causación.
        key: "product_codes",
        label: "Producto",
        type: "multiselect",
        width: 320,
        placeholder: "Todos",
        optionsSource: "/paracodes/form_accountingmodel/",
        optionValue: "codeNumber",
        optionLabel: model => {
          const doc = paracodeDoc(model);
          const id = model.codeNumber || "";
          const name = model.codeTitle || doc.name || doc.nombre_compuesto || "";
          return name ? `${id} - ${name}` : String(id);
        }
      },
      periodDateRangeFilter
    ],
    exportable: true,
    paginated: true,
    defaultPageSize: 200,
    stickyHeader: true,
    autoLoad: true,
    permiso: "reports.purchases_by_product.view"
  },
  {
    slug: "voucher_details",
    titulo: "Comprobantes detallados",
    descripcion: "Cada comprobante desglosado en los movimientos contables que lo componen.",
    endpoint: "reports/voucher-details",
    categoria: "Comprobantes",
    // Las tres claves del encabezado del comprobante se muestran como fila de
    // grupo (no como columnas), quedando la tabla enfocada en el movimiento.
    // Siguen viajando al Excel gracias a hideInTable.
    columnas: [
      { key: "voucherNumber", label: "# Comprobante", hideInTable: true },
      { key: "transactionType", label: "Tipo de transacción", hideInTable: true },
      { key: "elaborationDate", label: "Fecha elaboración", format: "date", hideInTable: true },
      { key: "accountCode", label: "Código contable" },
      { key: "accountName", label: "Cuenta contable" },
      { key: "accountDescription", label: "Descripción cuenta" },
      { key: "party", label: "Tercero" },
      { key: "movementDetail", label: "Detalle movimiento" },
      { key: "debit", label: "Valor débito", format: "decimal", align: "right" },
      { key: "credit", label: "Valor crédito", format: "decimal", align: "right" },
      // Totales del comprobante completo (los calcula el SQL con ventana, así
      // que son correctos aunque el comprobante quede partido entre páginas).
      // Se muestran en el encabezado del grupo; en la tabla sobrarían.
      { key: "voucherDebit", label: "Débito comprobante", format: "decimal", align: "right", hideInTable: true },
      { key: "voucherCredit", label: "Crédito comprobante", format: "decimal", align: "right", hideInTable: true }
    ],
    // Un grupo colapsable por comprobante, con encabezado enriquecido.
    // Las columnas NO son ordenables: reordenar fragmentaría los grupos
    // (el agrupador del frontend hace una sola pasada lineal).
    grouping: {
      levels: [
        {
          key: "voucherNumber",
          className: "is-group-level-0",
          label: row => {
            if (!row) return "";
            const partes = [row.voucherNumber || "(sin número)"];
            if (row.transactionType) partes.push(row.transactionType);
            if (row.elaborationDate) partes.push(row.elaborationDate);
            return partes.join("  ·  ");
          }
        }
      ],
      // Fila de cierre por comprobante. Los valores NO se suman en el frontend:
      // se toman de voucherDebit/voucherCredit, que el SQL calcula por ventana
      // sobre todo el conjunto filtrado, así el total es el del comprobante
      // completo aunque quede partido entre páginas.
      totalsLabel: "Total comprobante",
      totals: { debit: "voucherDebit", credit: "voucherCredit" }
    },
    filtros: buildVoucherFilters(true),
    exportable: true,
    paginated: true,
    defaultPageSize: 200,
    stickyHeader: true,
    autoLoad: true,
    permiso: "reports.voucher_details.view"
  },
  {
    slug: "voucher_sequence",
    titulo: "Consecutivo de comprobantes",
    descripcion: "Un renglón por comprobante, para revisar la continuidad del consecutivo por tipo.",
    endpoint: "reports/voucher-sequence",
    categoria: "Comprobantes",
    columnas: [
      { key: "transactionType", label: "Tipo de transacción", sortable: true, sticky: true, width: 240 },
      { key: "voucherNumber", label: "# Comprobante", sortable: true },
      { key: "amount", label: "Valor", format: "decimal", align: "right", sortable: true },
      { key: "elaborationDate", label: "Fecha elaboración", format: "date", sortable: true },
      // Distinta de la de elaboración: cuándo se registró el documento en el
      // sistema (permite detectar registros extemporáneos).
      { key: "creationDate", label: "Fecha creación", format: "date", sortable: true }
    ],
    filtros: buildVoucherFilters(true),
    exportable: true,
    paginated: true,
    defaultPageSize: 200,
    stickyHeader: true,
    autoLoad: true,
    permiso: "reports.voucher_sequence.view"
  },
  {
    slug: "unposted_vouchers",
    titulo: "Comprobantes no contabilizados",
    descripcion: "Borradores pendientes de contabilizar. Permite contabilizarlos o borrarlos en lote.",
    endpoint: "reports/unposted-vouchers",
    categoria: "Comprobantes",
    columnas: [
      { key: "docId", label: "docId", hideInTable: true },
      { key: "transactionType", label: "Tipo de transacción", sortable: true, sticky: true, width: 260 },
      { key: "party", label: "Tercero", sortable: true },
      { key: "elaborationDate", label: "Fecha elaboración", format: "date", sortable: true },
      { key: "isFormValid", label: "Formulario válido", format: "boolean", sortable: true },
      {
        key: "_actions",
        label: "Acciones",
        rowActions: [
          {
            key: "post",
            label: "Contabilizar",
            icon: "check_circle",
            endpoint: "/vouchers/post",
            confirm: "¿Contabilizar este comprobante?",
            // Un borrador incompleto será rechazado por el backend: se
            // deshabilita antes para no hacer viajar un error evitable.
            disabledWhen: row => !(row && row.isFormValid)
          },
          {
            key: "delete",
            label: "Borrar",
            icon: "delete",
            danger: true,
            endpoint: "/vouchers/delete",
            confirm: "¿Borrar este comprobante? El borrado es lógico y sólo aplica a borradores."
          }
        ]
      }
    ],
    filtros: buildVoucherFilters(true),
    // Selección múltiple + acciones en lote sobre los seleccionados.
    selection: {
      rowKey: "docId",
      actions: [
        {
          key: "post",
          label: "Contabilizar seleccionados",
          icon: "check_circle",
          endpoint: "/vouchers/post",
          confirm: "¿Contabilizar los {n} comprobantes seleccionados?"
        },
        {
          key: "delete",
          label: "Borrar seleccionados",
          icon: "delete",
          danger: true,
          endpoint: "/vouchers/delete",
          confirm: "¿Borrar los {n} comprobantes seleccionados? El borrado es lógico y sólo aplica a borradores."
        }
      ]
    },
    exportable: true,
    paginated: true,
    defaultPageSize: 200,
    stickyHeader: true,
    autoLoad: true,
    permiso: "reports.unposted_vouchers.view"
  },
  {
    slug: "fixed_assets_register",
    titulo: "Registro de activos fijos",
    descripcion:
      "Inventario de activos con costo, depreciación acumulada y valor neto en libros al corte.",
    endpoint: "reports/fixed-assets-register",
    categoria: "Activos fijos",
    columnas: [
      { key: "assetCode", label: "Código", sortable: true, sticky: true, width: 130 },
      { key: "assetName", label: "Activo", sortable: true, sticky: true, width: 230 },
      { key: "groupCode", label: "Cód. grupo", sortable: true },
      { key: "groupName", label: "Grupo", sortable: true },
      { key: "acquisitionDate", label: "Adquisición", format: "date", sortable: true },
      { key: "startDate", label: "Inicio depreciación", format: "date", sortable: true },
      { key: "cost", label: "Costo", format: "money", align: "right", sortable: true },
      { key: "salvageValue", label: "Salvamento", format: "money", align: "right" },
      { key: "depreciableBase", label: "Base depreciable", format: "money", align: "right", sortable: true },
      { key: "lifeMonths", label: "Vida (meses)", align: "right" },
      { key: "monthsDepreciated", label: "Meses depreciados", align: "right" },
      { key: "remainingMonths", label: "Meses restantes", align: "right", sortable: true },
      { key: "monthlyQuota", label: "Cuota mensual", format: "money", align: "right", sortable: true },
      {
        key: "accumulatedDepreciation",
        label: "Depreciación acumulada",
        format: "money",
        align: "right",
        sortable: true
      },
      { key: "netBookValue", label: "Valor neto en libros", format: "money", align: "right", sortable: true },
      { key: "depreciatedPct", label: "% depreciado", format: "percentage", align: "right", sortable: true },
      { key: "lastPeriod", label: "Último periodo", format: "period" },
      { key: "profitCenterCode", label: "Cód. centro de costos" },
      { key: "profitCenter", label: "Centro de costos", sortable: true },
      { key: "partyIdentifier", label: "NIT tercero" },
      { key: "partyName", label: "Tercero" }
    ],
    filtros: [
      bookFilter,
      {
        key: "as_of_period",
        label: "Corte",
        type: "select",
        width: 200,
        options: buildPeriodOptions(),
        default: currentPeriodCode
      },
      ...fixedAssetScopeFilters,
      { key: "only_active", label: "Sólo vigentes", type: "boolean", width: 40, default: true }
    ],
    // El `summary` del backend abarca TODO el conjunto filtrado, no la página:
    // por eso encabeza la pantalla como tarjetas y no sólo como fila de totales.
    summaryCards: [
      { key: "assets", label: "Activos", format: "number" },
      { key: "cost", label: "Costo", format: "money" },
      { key: "accumulatedDepreciation", label: "Depreciación acumulada", format: "money", tone: "warn" },
      { key: "netBookValue", label: "Valor neto en libros", format: "money", tone: "ok" },
      { key: "monthlyQuota", label: "Cuota mensual", format: "money" }
    ],
    exportable: true,
    paginated: true,
    defaultPageSize: 200,
    stickyHeader: true,
    autoLoad: true,
    permiso: "reports.fixed_assets_register.view"
  },
  {
    slug: "depreciation_by_period",
    titulo: "Depreciación por periodo",
    descripcion:
      "Cuotas de depreciación causadas en cada periodo, con el comprobante que las contabilizó.",
    endpoint: "reports/depreciation-by-period",
    categoria: "Activos fijos",
    columnas: [
      { key: "period", label: "Periodo", format: "period", sortable: true, sticky: true, width: 130 },
      { key: "assetCode", label: "Código", sortable: true, sticky: true, width: 120 },
      { key: "assetName", label: "Activo", sortable: true },
      { key: "book", label: "Libro", format: "book" },
      { key: "groupCode", label: "Cód. grupo", sortable: true },
      { key: "groupName", label: "Grupo", sortable: true },
      { key: "monthIndex", label: "Mes n.º", align: "right", sortable: true },
      { key: "lifeMonths", label: "Vida (meses)", align: "right" },
      { key: "cost", label: "Costo", format: "money", align: "right" },
      { key: "salvageValue", label: "Salvamento", format: "money", align: "right" },
      { key: "depreciableBase", label: "Base depreciable", format: "money", align: "right" },
      { key: "amount", label: "Cuota del periodo", format: "money", align: "right", sortable: true },
      {
        key: "accumulatedDepreciation",
        label: "Depreciación acumulada",
        format: "money",
        align: "right",
        sortable: true
      },
      { key: "netBookValue", label: "Valor neto", format: "money", align: "right", sortable: true },
      { key: "expenseAccount", label: "Cuenta gasto" },
      { key: "accumulatedAccount", label: "Cuenta acumulada" },
      { key: "profitCenterCode", label: "Centro de costos" },
      { key: "movementDate", label: "Fecha movimiento", format: "date", sortable: true },
      { key: "docId", label: "docId", hideInTable: true },
      {
        key: "voucherNumber",
        label: "# Comprobante",
        sortable: true,
        // Abre el comprobante que contabilizó la cuota, para cuadrar contra el
        // libro sin tener que buscarlo por número.
        href: row => (row && row.docId ? `/cases/${row.docId}` : null),
        hrefTitle: "Abrir el comprobante en una pestaña nueva"
      },
      { key: "voucherState", label: "Estado comprobante", format: "voucherState" }
    ],
    filtros: [
      bookFilter,
      {
        key: "start_period",
        label: "Periodo inicial",
        type: "select",
        width: 200,
        options: buildPeriodOptions(),
        default: () => {
          const y = new Date().getFullYear();
          return `${y}01`;
        }
      },
      {
        key: "end_period",
        label: "Periodo final",
        type: "select",
        width: 200,
        options: buildPeriodOptions(),
        default: () => {
          const y = new Date().getFullYear();
          return `${y}12`;
        }
      },
      ...fixedAssetScopeFilters
    ],
    summaryCards: [
      { key: "lines", label: "Líneas", format: "number" },
      { key: "assets", label: "Activos", format: "number" },
      { key: "periods", label: "Periodos", format: "number" },
      { key: "amount", label: "Depreciación del rango", format: "money", tone: "ok" }
    ],
    exportable: true,
    paginated: true,
    defaultPageSize: 200,
    stickyHeader: true,
    autoLoad: true,
    permiso: "reports.depreciation_by_period.view"
  }
];

export function getReportBySlug(slug) {
  return reports.find(r => r.slug === slug) || null;
}

export function getReportsByCategory() {
  return reports.reduce((acc, report) => {
    const cat = report.categoria || "Sin categoría";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(report);
    return acc;
  }, {});
}

export default reports;
